import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Loader2 } from "lucide-react";
import { CONTACT, EVENT_TYPES } from "@/lib/data";
import Reveal from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", event_type: "", event_date: "", guest_count: "", message: "" };

const inputCls =
  "w-full bg-transparent border-b border-[#FDFBF7]/20 focus:border-[#D4AF37] outline-none py-3 font-body text-[#FDFBF7] placeholder:text-[#A89F91]/60 transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.event_type) {
      toast.error("Please fill in your name, email, phone and event type.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Thank you! We'll be in touch within 24 hours to plan your feast.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + CONTACT.phone);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-40 bg-[#0A0607]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[1.15fr_0.85fr] gap-16 lg:gap-24">
        <div>
          <Reveal>
            <p className="font-body text-xs tracking-[0.4em] uppercase text-[#D4AF37] mb-4">Book Your Event</p>
            <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl text-[#FDFBF7] leading-[0.95] mb-12">
              Let's plan your <span className="gold-gradient-text italic">feast.</span>
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="booking-form" className="space-y-8">
              <div className="grid sm:grid-cols-2 gap-8">
                <input data-testid="input-name" className={inputCls} placeholder="Full Name *" value={form.name} onChange={set("name")} />
                <input data-testid="input-phone" className={inputCls} placeholder="Phone *" value={form.phone} onChange={set("phone")} />
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                <input data-testid="input-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} />
                <select data-testid="input-event-type" className={`${inputCls} appearance-none cursor-pointer`} value={form.event_type} onChange={set("event_type")}>
                  <option value="" className="bg-[#140A0D]">Event Type *</option>
                  {EVENT_TYPES.map((t) => (
                    <option key={t} value={t} className="bg-[#140A0D]">{t}</option>
                  ))}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-8">
                <input data-testid="input-date" type="date" className={`${inputCls} [color-scheme:dark]`} value={form.event_date} onChange={set("event_date")} />
                <input data-testid="input-guests" className={inputCls} placeholder="Approx. Guests" value={form.guest_count} onChange={set("guest_count")} />
              </div>
              <textarea data-testid="input-message" rows={3} className={`${inputCls} resize-none`} placeholder="Tell us about your event..." value={form.message} onChange={set("message")} />

              <button
                data-testid="booking-submit"
                type="submit"
                disabled={loading}
                className="group relative px-10 py-4 bg-[#D4AF37] text-[#0A0607] font-body text-sm tracking-[0.2em] uppercase disabled:opacity-60 flex items-center gap-3"
              >
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Sending..." : "Request a Quote"}
              </button>
            </form>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="lg:pt-32 space-y-10">
            <a href={CONTACT.phoneHref} data-testid="contact-phone" className="group flex items-start gap-5">
              <span className="w-12 h-12 shrink-0 rounded-full border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#0A0607] transition-colors duration-300"><Phone size={18} /></span>
              <span>
                <span className="block font-body text-xs tracking-[0.2em] uppercase text-[#A89F91] mb-1">Call Us</span>
                <span className="font-display text-2xl md:text-3xl text-[#FDFBF7] group-hover:text-[#D4AF37] transition-colors">{CONTACT.phone}</span>
              </span>
            </a>
            <a href={CONTACT.emailHref} data-testid="contact-email" className="group flex items-start gap-5">
              <span className="w-12 h-12 shrink-0 rounded-full border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#0A0607] transition-colors duration-300"><Mail size={18} /></span>
              <span>
                <span className="block font-body text-xs tracking-[0.2em] uppercase text-[#A89F91] mb-1">Email</span>
                <span className="font-display text-xl md:text-2xl text-[#FDFBF7] group-hover:text-[#D4AF37] transition-colors break-all">{CONTACT.email}</span>
              </span>
            </a>
            <div className="flex items-start gap-5">
              <span className="w-12 h-12 shrink-0 rounded-full border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37]"><MapPin size={18} /></span>
              <span>
                <span className="block font-body text-xs tracking-[0.2em] uppercase text-[#A89F91] mb-1">Service Area</span>
                <span className="font-display text-2xl md:text-3xl text-[#FDFBF7]">{CONTACT.area}</span>
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
